import { FC, useRef, useState } from 'react';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import { Button } from 'ui-kit';

import { formatCurrency } from 'configs/columns';

import {
  BidTerms,
  bookValueAsOfLabel,
  bookValueCents,
  formatPctE4,
  priceEquivalentLabel,
  priceLabel,
  SaleOffering,
  STRUCTURE_LABEL,
} from './saleBids';
import { formatCentralTime } from './salesUtils';
import { PRIMARY_BUTTON_LIGHT_THEME, SECONDARY_BUTTON_LIGHT_THEME } from './styles';

export const BINDING_BID_COPY =
  "This bid is binding. Once submitted it is your offer to purchase and can't be changed or withdrawn (BI §14). The FDIC is entitled to rely on its accuracy (BC §6).";

const PriceSummary: FC<{
  terms: BidTerms;
  selectedOfferings: SaleOffering[];
  calculationDate: string;
}> = ({ terms, selectedOfferings, calculationDate }) => {
  const equivalent = priceEquivalentLabel(
    terms,
    bookValueCents(selectedOfferings),
    calculationDate,
  );
  return (
    <>
      <div>
        {terms.pct_e4 != null
          ? `${formatPctE4(terms.pct_e4)}% ${bookValueAsOfLabel(calculationDate)}`
          : formatCurrency(terms.amount_cents)}
      </div>
      {equivalent && <div className="text-gray-400 light:text-gray-700">{equivalent}</div>}
    </>
  );
};

export const SaleBidConfirmDialog: FC<{
  terms: BidTerms;
  offerings: SaleOffering[];
  deadlineAt: string;
  calculationDate: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}> = ({ terms, offerings, deadlineAt, calculationDate, submitting, onCancel, onSubmit }) => {
  const [typedValue, setTypedValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const headingId = useId();
  const inputId = useId();
  const promptId = useId();

  const { refs, context } = useFloating({
    open: true,
    onOpenChange: (open) => {
      if (!open) {
        onCancel();
      }
    },
  });
  // Escape only: a stray click outside shouldn't throw away a half-typed confirmation. Not while
  // the bid is in flight: closing can't recall it.
  const dismiss = useDismiss(context, { outsidePress: false, enabled: !submitting });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const canonicalValue = priceLabel(terms);
  const confirmed = typedValue.trim() === canonicalValue;
  const selectedOfferings = terms.offering_ids
    .map((offeringId) => offerings.find((offering) => offering.offering_id === offeringId))
    .filter((offering): offering is SaleOffering => offering != null);

  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll className="z-50 flex items-center justify-center bg-black/80">
        <FloatingFocusManager context={context} initialFocus={inputRef}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            aria-modal="true"
            aria-labelledby={headingId}
            data-testid="sale-bid-confirm-dialog"
            className="w-[min(560px,92vw)] rounded-lg border border-slate-500 bg-background-canvas p-6 text-left text-white shadow-lg light:border-gray-200 light:bg-white light:text-gray-900"
          >
            <h2 id={headingId} className="font-heebo text-lg font-medium">
              Submit final bid
            </h2>

            <dl className="mt-4 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-gray-400 light:text-gray-700">Round</dt>
              <dd>{terms.round}</dd>
              <dt className="text-gray-400 light:text-gray-700">Structure</dt>
              <dd>{STRUCTURE_LABEL[terms.structure]}</dd>
              <dt className="text-gray-400 light:text-gray-700">Deadline</dt>
              <dd>{formatCentralTime(deadlineAt)}</dd>
              <dt className="text-gray-400 light:text-gray-700">Pools</dt>
              <dd>
                <ul>
                  {selectedOfferings.map((offering) => (
                    <li key={offering.offering_id} className="whitespace-pre">
                      <span className="font-mono">{offering.fdic_pool_number}</span>
                      {'  '}
                      {offering.offering_name}
                    </li>
                  ))}
                </ul>
                {selectedOfferings.length > 1 && (
                  <div className="text-gray-400 light:text-gray-700">(linked: all or none)</div>
                )}
              </dd>
              <dt className="text-gray-400 light:text-gray-700">Price</dt>
              <dd>
                <PriceSummary
                  terms={terms}
                  selectedOfferings={selectedOfferings}
                  calculationDate={calculationDate}
                />
              </dd>
            </dl>

            <p className="mt-4 text-sm font-medium">{BINDING_BID_COPY}</p>

            <p id={promptId} className="mt-4 text-sm">
              To confirm, enter your bid:{' '}
              {/* user-select: all, so one click takes the whole value: double-click word
                  selection stops at '.', ',' and '%'. */}
              <code
                data-testid="sale-bid-confirm-value"
                className="select-all rounded bg-background-surface px-2 py-0.5 font-mono light:bg-gray-100"
                style={{ userSelect: 'all' }}
              >
                {canonicalValue}
              </code>
            </p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (confirmed && !submitting) {
                  onSubmit();
                }
              }}
            >
              <label
                htmlFor={inputId}
                className="mt-2 block text-xs font-bold text-gray-400 light:text-gray-700"
              >
                Your bid
              </label>
              <input
                id={inputId}
                ref={inputRef}
                type="text"
                autoComplete="off"
                spellCheck={false}
                data-testid="sale-bid-confirm-input"
                aria-describedby={promptId}
                value={typedValue}
                onChange={(event) => setTypedValue(event.target.value)}
                className="mt-1 w-full rounded border border-gray-600 bg-background-surface px-3 py-1.5 font-mono text-white outline-none focus:border-accent-default light:bg-white light:text-gray-900 light:focus:border-pink-700"
              />

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  size="small"
                  type="secondary"
                  className={submitting ? '' : SECONDARY_BUTTON_LIGHT_THEME}
                  label="Cancel"
                  disabled={submitting}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="primary"
                  label={`Submit final bid of ${canonicalValue}`}
                  data-testid="sale-bid-confirm-submit"
                  className={!confirmed || submitting ? '' : PRIMARY_BUTTON_LIGHT_THEME}
                  buttonType="submit"
                  disabled={!confirmed || submitting}
                >
                  Submit final bid of {canonicalValue}
                </Button>
              </div>
            </form>
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
};
